/**
 * How many documents an answer's references actually amount to.
 *
 * `reference` is a list of *page* hits, so one document appears once per cited
 * page — 48 references routinely resolve to a handful of documents. The panel
 * beside the chat deduplicates before rendering (`addLumaDocs`), so the count
 * on the button has to deduplicate the same way or it promises far more than
 * the table shows.
 */

/** The shape `addLumaDocs` reads: a measure id where known, a document id otherwise. */
export interface LumaReferenceLike {
   measure_id?: unknown;
   document_id?: unknown;
   docid?: unknown;
}

/**
 * Distinct documents behind a reference list.
 *
 * Mirrors `addLumaDocs`: references carrying a usable `measure_id` are
 * deduplicated on it, and the rest fall back to their document id — those are
 * kept rather than dropped, because the answer already cited them.
 */
export function referenceDocumentCount(
   references: readonly LumaReferenceLike[] | null | undefined,
): number {
   if (!references?.length)
      return 0;

   const measureIds = new Set<number>();
   const documentIds = new Set<string>();

   for (const ref of references) {
      const measureId = Number(ref?.measure_id);
      if (Number.isFinite(measureId) && measureId > 0) {
         measureIds.add(measureId);
         continue;
      }
      documentIds.add(String(ref?.document_id ?? ref?.docid ?? ''));
   }

   return measureIds.size + documentIds.size;
}
